import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import LanguageSwitcher from '../components/LanguageSwitcher'
import DarkModeToggle from '../components/DarkModeToggle'

function Login() {
  const { t } = useTranslation()
  const { signInWithGitHub, signInWithGitLab } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleGitHubLogin = async () => {
    try {
      setLoading(true)
      setError(null)
      await signInWithGitHub()
    } catch (err) {
      console.error('GitHub login error:', err)
      setError(err.message || t('login.errors.loginFailed'))
      setLoading(false)
    }
  }

  const handleGitLabLogin = async () => {
    try {
      setLoading(true)
      setError(null)
      await signInWithGitLab()
    } catch (err) {
      console.error('GitLab login error:', err)
      setError(err.message || t('login.errors.loginFailed'))
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      {/* Controls - Top Right */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <DarkModeToggle />
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12 transition-colors">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold mb-3 dark:text-white">
              🃏 {t('login.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-lg">
              {t('login.subtitle')}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 rounded-lg">
              {error}
            </div>
          )}

          {/* Login Buttons */}
          <div className="space-y-4">
            {/* GitHub Login */}
            <button
              onClick={handleGitHubLogin}
              disabled={loading}
              className="w-full bg-gray-900 hover:bg-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 text-white rounded-xl p-4 transition-all transform hover:scale-105 shadow-lg font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <div className="flex items-center justify-center gap-3">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    fillRule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{t('login.signInWithGitHub')}</span>
              </div>
            </button>

            {/* GitLab Login */}
            <button
              onClick={handleGitLabLogin}
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white rounded-xl p-4 transition-all transform hover:scale-105 shadow-lg font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <div className="flex items-center justify-center gap-3">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.546 10.93L13.067.452c-.604-.603-1.582-.603-2.188 0L.452 10.93c-.6.605-.6 1.584 0 2.188l10.427 10.426c.605.604 1.582.604 2.188 0l10.48-10.425c.6-.604.6-1.582 0-2.187zM12 18.843l-1.938-5.969H8.595l3.405-4.688 3.404 4.688h-1.466L12 18.843z" />
                </svg>
                <span>{t('login.signInWithGitLab')}</span>
              </div>
            </button>
          </div>

          {/* Info */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              {t('login.info')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
